import { DecorationSet } from '@codemirror/view';

import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';

import { RunContents, useExamples } from '@/hooks/useSwr';
import { ExampleSchema, RunSchema, RunStatsSchema } from '@/types/schema';
import { backendUrl } from '@/utils/constants';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { useDetectXSPanel } from '../utils/useDetectXSPanel';
import { useUserPrefs } from '../utils/useUserPrefs';
import { RunDetailsNavigation } from './RunDetailsNavigation';
import { RunDetailsPanel } from './RunDetailsPanel';
import { RunTreeSkeleton } from './RunTreeSkeleton';
import { SIDEBAR_LOCAL_STORAGE_KEY } from './constants';

function PanelHandle() {
  return (
    <PanelResizeHandle className="group relative flex items-stretch text-secondary">
      <div className="w-[1px] bg-tertiary" />
      <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
      <div className="absolute inset-y-0 w-[2px] bg-brand-green-100 opacity-0 transition-all group-hover:opacity-100 group-data-[resize-handle-active]:bg-brand-green-400 group-data-[resize-handle-active]:opacity-100" />
    </PanelResizeHandle>
  );
}

export function RunDetails(props: {
  shareToken?: string;
  datasetShareToken?: string;

  run?: RunSchema;
  runContents?: RunContents;

  rootRun?: RunSchema;
  childRunLookup?: Record<string, RunSchema[] | undefined>;

  example?: ExampleSchema | undefined;
  examplePath?: string;

  onNavigate: (run: RunSchema) => void;

  insidePane?: boolean;
  className?: string;

  searchFilter: string | undefined;
  onSearchFilterChange: (value: string | undefined) => void;
  filteredRuns: RunSchema[] | undefined;

  isRunLoading: boolean;
  isChildRunsLoading: boolean;
  isFilteredRunsLoading: boolean;
  comparePosition?: 'left' | 'right';
  scrollRef?: React.RefObject<HTMLDivElement>;
  forceRawRender?: 'raw' | 'rendered';
  getDiffValues?: (type: 'inputs' | 'outputs') =>
    | {
        decoration: DecorationSet;
        generatedCode: string | undefined;
      }
    | undefined;
  showAllRuns?: boolean;
  statsForLatency?: RunStatsSchema;
}) {
  const isPublicPage =
    props.shareToken != null || props.datasetShareToken != null;

  const [sidebarVisible, setSidebarVisible] = useLocalStorageState(
    SIDEBAR_LOCAL_STORAGE_KEY,
    true
  );

  const exampleFetch = useExamples(
    props.rootRun?.reference_example_id
      ? {
          id: [props.rootRun.reference_example_id],
          select: ['attachment_urls'],
          as_of: props.rootRun.start_time,
        }
      : null
  );
  const rootExample = {
    data: exampleFetch.data?.[0],
    isLoading: exampleFetch.isLoading,
  };
  const presignedUrls = useMemo(
    () =>
      props.rootRun?.inputs?.attachments
        ? Object.entries(props.rootRun.inputs.attachments).reduce(
            (acc, [key, value]) => {
              if (
                typeof value === 'object' &&
                typeof value?.['presigned_url'] === 'string'
              ) {
                acc[value['presigned_url']] = rootExample.data
                  ?.attachment_urls?.['attachment.' + key]
                  ?.presigned_url as string;
              }
              return acc;
            },
            {} as Record<string, string>
          )
        : {},
    [props.rootRun?.inputs?.attachments, rootExample.data?.attachment_urls]
  );

  let usesExampleAttachmentsAsInput = false;
  if (props.run?.inputs?.attachments) {
    const attachments = props.run?.inputs.attachments;
    if (typeof attachments === 'object' && attachments !== null) {
      usesExampleAttachmentsAsInput = Object.values(attachments).every(
        (value) =>
          typeof value === 'object' &&
          value !== null &&
          'presigned_url' in value &&
          'reader' in value
      );
    }
  }

  const inputs = useMemo(() => {
    let inputs = props.runContents?.inputs.data ?? props.run?.inputs;
    if (usesExampleAttachmentsAsInput && inputs) {
      inputs = Object.fromEntries(
        Object.entries(inputs).filter(([key]) => key !== 'attachments')
      );
    }
    if (inputs && presignedUrls) {
      const replacePresignedUrls = (obj: any, depth: number): any => {
        if (depth > 10) {
          return obj;
        }
        if (
          typeof obj === 'string' &&
          obj.startsWith(`${backendUrl}/public/download`)
        ) {
          return presignedUrls?.[obj] || obj;
        } else if (Array.isArray(obj)) {
          return obj.map((o) => replacePresignedUrls(o, depth + 1));
        } else if (typeof obj === 'object' && obj !== null) {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
              key,
              replacePresignedUrls(value, depth + 1),
            ])
          );
        }
        return obj;
      };

      inputs = replacePresignedUrls(inputs, 0);
    }
    return inputs;
  }, [
    props.runContents?.inputs.data,
    props.run?.inputs,
    usesExampleAttachmentsAsInput,
    presignedUrls,
  ]);

  const outputs = props.runContents?.outputs.data ?? props.run?.outputs;
  const error = props.runContents?.error.data ?? props.run?.error;
  const updatedRun = useMemo(() => {
    return props.run
      ? {
          ...props.run,
          inputs,
          outputs,
          error,
        }
      : undefined;
  }, [error, inputs, outputs, props.run]);

  const inputsLoading = props.runContents?.inputs.loading;
  const outputsLoading = props.runContents?.outputs.loading;
  const errorLoading = props.runContents?.error.loading;

  const rootRuns = useMemo(() => {
    return props.rootRun ? [props.rootRun] : [];
  }, [props.rootRun]);

  const userPrefs = useUserPrefs({
    comparePosition: props.comparePosition,
    searchFilter: props.searchFilter,
    filteredRuns: props.filteredRuns,
  });

  const treePanelRef = useRef<ImperativePanelHandle>(null);

  const treePanelDefaultSize = props.insidePane ? 30 : 40;
  const treePanelMinSize = userPrefs.showGraphStats ? 30 : 6;
  const treePanelMaxSize = userPrefs.showGraphStats ? 50 : treePanelDefaultSize;
  const treePanelRefSize = treePanelRef.current?.getSize();

  const toggleGraphStats = (show: boolean) => {
    userPrefs.setShowGraphStats(show);
    if (show) {
      setSidebarVisible(false);
    }
    setTimeout(() => {
      treePanelRef.current?.resize(show ? 50 : treePanelDefaultSize);
    }, 0);
  };

  const togglePanelSize = () => {
    const prevSize = treePanelRef.current?.getSize();
    if (prevSize) {
      const isCollapse = prevSize > 6;
      if (isCollapse) {
        userPrefs.setShowGraphStats(false);
      }
      setTimeout(() => {
        treePanelRef.current?.resize(isCollapse ? 6 : 30);
      }, 0);
    }
  };
  useLayoutEffect(() => {
    let timeoutId;
    if (
      props.comparePosition &&
      treePanelRef.current &&
      // when panel is not rendered yet, getSize is undefined, only resize if this is the case
      !treePanelRefSize
    ) {
      timeoutId = setTimeout(() => {
        treePanelRef.current?.resize(6);
        setSidebarVisible(false);
      }, 0);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [props.comparePosition, treePanelRefSize, setSidebarVisible]);

  const panelContainerRef = useRef<HTMLDivElement>(null);
  const isXsPanel = useDetectXSPanel({
    panelId: 'tree-panel',
    panelRef: panelContainerRef,
  });

  const showLoading = props.isChildRunsLoading || props.isFilteredRunsLoading; // public runs don't use streaming endpoint so use isChildRunsLoading to show loading skeleton
  return (
    <div className={cn('flex flex-col', props.className)}>
      <PanelGroup
        autoSaveId={`run-detail-${props.insidePane ? 'pane' : 'page'}`}
        direction="horizontal"
        className="flex-grow"
      >
        <Panel
          id="tree-panel"
          order={1}
          ref={treePanelRef}
          minSize={treePanelMinSize}
          maxSize={props.insidePane ? treePanelMaxSize : undefined}
          defaultSize={treePanelDefaultSize}
          className="relative flex min-w-[115px] flex-col"
        >
          <div ref={panelContainerRef} className="h-full">
            {showLoading ? (
              <RunTreeSkeleton isXsPanel={isXsPanel} hasWaterfall={true} />
            ) : (
              props.run &&
              props.rootRun &&
              (props.childRunLookup || isPublicPage) && (
                <RunDetailsNavigation
                  isPublicPage={isPublicPage}
                  run={props.run}
                  rootRuns={rootRuns}
                  searchFilter={props.searchFilter}
                  onSearchFilterChange={props.onSearchFilterChange}
                  filteredRuns={props.filteredRuns}
                  childRunLookup={props.childRunLookup || {}}
                  onNavigate={props.onNavigate}
                  insidePane={props.insidePane}
                  togglePanelSize={togglePanelSize}
                  comparePosition={props.comparePosition}
                  showAllRuns={props.showAllRuns}
                  userPrefs={userPrefs}
                  toggleGraphStats={toggleGraphStats}
                  isXsPanel={isXsPanel}
                />
              )
            )}
          </div>
        </Panel>
        <PanelHandle />
        <Panel
          id="run-details-panel"
          className="flex min-w-0 flex-col !overflow-visible pt-4"
          order={2}
        >
          <RunDetailsPanel
            key={props.run?.id}
            run={updatedRun}
            example={props.example}
            examplePath={props.examplePath}
            isRunLoading={props.isRunLoading}
            isPublicPage={isPublicPage}
            shareToken={props.shareToken}
            datasetShareToken={props.datasetShareToken}
            sidebarVisible={sidebarVisible}
            setSidebarVisible={setSidebarVisible}
            isComparing={Boolean(props.comparePosition)}
            scrollRef={props.scrollRef}
            forceRawRender={props.forceRawRender}
            getDiffValues={props.getDiffValues}
            inputsLoading={inputsLoading}
            outputsLoading={outputsLoading}
            errorLoading={errorLoading}
            showSeparateAttachments={usesExampleAttachmentsAsInput}
            statsForLatency={props.statsForLatency}
          />
        </Panel>
      </PanelGroup>
    </div>
  );
}
